export interface RuntimeConfig {
    dipBaseUrl: string;
    dipClientId: string;
    audience: string;
    apiBaseUrl: string;
    termsOfUseUrl: string;
    privacyPolicyUrl: string;
    profileUrl: string;
    powerSourceUrl: string;
    plus1ForumUrl: string;
}

type Environment = Readonly<Record<string, string | undefined>>;

const isUsableValue = (value: string | undefined): value is string => {
    if (!value) return false;
    const trimmedValue = value.trim();
    return trimmedValue !== '' && !/^\$\{[^}]+\}$/.test(trimmedValue);
};

const getValue = (
    runtimeConfig: Readonly<Partial<Record<string, string>>> | undefined,
    environment: Environment,
    key: string,
    aliases: string[] = [],
): string | undefined => {
    const runtimeValue = runtimeConfig?.[key];
    if (isUsableValue(runtimeValue)) return runtimeValue.trim();

    for (const environmentKey of [key, ...aliases]) {
        const localValue = environment[environmentKey];
        if (isUsableValue(localValue)) return localValue.trim();
    }

    return undefined;
};

const getRequiredValue = (
    runtimeConfig: Readonly<Partial<Record<string, string>>> | undefined,
    environment: Environment,
    key: string,
    aliases: string[] = [],
): string => {
    const value = getValue(runtimeConfig, environment, key, aliases);
    if (!value) {
        throw new Error(`Missing required frontend configuration: ${key}`);
    }
    return value;
};

const getRequiredUrl = (
    runtimeConfig: Readonly<Partial<Record<string, string>>> | undefined,
    environment: Environment,
    key: string,
    aliases: string[] = [],
): string => {
    const value = getRequiredValue(runtimeConfig, environment, key, aliases);
    try {
        const url = new URL(value);
        if (!['http:', 'https:'].includes(url.protocol)) throw new Error();
    } catch {
        throw new Error(`Invalid frontend configuration URL: ${key}`);
    }
    return value;
};

export const resolveRuntimeConfig = (
    runtimeConfig: Readonly<Partial<Record<string, string>>> | undefined,
    environment: Environment,
): RuntimeConfig => ({
    dipBaseUrl: getRequiredUrl(runtimeConfig, environment, 'REACT_APP_DIP_BASE_URL'),
    dipClientId: getRequiredValue(runtimeConfig, environment, 'REACT_APP_DIP_CLIENT_ID'),
    audience: getRequiredUrl(runtimeConfig, environment, 'REACT_APP_AUDIENCE'),
    apiBaseUrl: getRequiredUrl(runtimeConfig, environment, 'REACT_APP_API_BASE_URL', ['REACT_APP_PLUS1_CHATBOT_API_BASE_URL']),
    termsOfUseUrl: getRequiredUrl(runtimeConfig, environment, 'REACT_APP_TERMS_OF_USE_URL'),
    privacyPolicyUrl: getRequiredUrl(runtimeConfig, environment, 'REACT_APP_PRIVACY_POLICY_URL'),
    profileUrl: getRequiredUrl(runtimeConfig, environment, 'REACT_APP_YOUR_PROFILE_URL'),
    powerSourceUrl: getRequiredUrl(runtimeConfig, environment, 'REACT_APP_POWER_SOURCE_URL'),
    plus1ForumUrl: getRequiredUrl(runtimeConfig, environment, 'REACT_APP_PLUS1_FORUM_URL'),
});

const runtimeValues = typeof window !== 'undefined' ? window.__RUNTIME_CONFIG__ : undefined;

export const runtimeConfig = resolveRuntimeConfig(runtimeValues, process.env);

export default runtimeConfig;